import {
  addDoc,
  collection,
  serverTimestamp,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";

const ORDERS_COLLECTION = "orders";

export const createOrder = async ({ customer, items, total }) => {
  const orderData = {
    customer: {
      firstName: String(customer.firstName || "").trim(),
      lastName: String(customer.lastName || "").trim(),
      phone: String(customer.phone || "").trim(),
      address: String(customer.address || "").trim(),
    },
    items: items.map((i) => ({
      id: i.id,
      name: i.name,
      price: Number(i.price),
      quantity: i.quantity || 1,
      imageUrl: i.imageUrl || null,
    })),
    total: Number(total),
    status: "pending",
    createdAt: serverTimestamp(),
  };

  const ref = await addDoc(collection(db, ORDERS_COLLECTION), orderData);
  return ref.id;
};

/**
 * Fetch all orders, newest first. Sorted in memory to avoid composite index.
 */
export const getOrders = async () => {
  const snapshot = await getDocs(collection(db, ORDERS_COLLECTION));
  const orders = snapshot.docs.map((d) => ({
    id: d.id,
    ...d.data(),
  }));
  orders.sort((a, b) => {
    const ta = a.createdAt?.toMillis?.() ?? 0;
    const tb = b.createdAt?.toMillis?.() ?? 0;
    return tb - ta;
  });
  return orders;
};

/**
 * Subscribe to orders in real time (for admin dashboard).
 */
export const subscribeOrders = (onUpdate) => {
  return onSnapshot(collection(db, ORDERS_COLLECTION), (snapshot) => {
    const orders = snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    }));
    orders.sort((a, b) => {
      const ta = a.createdAt?.toMillis?.() ?? 0;
      const tb = b.createdAt?.toMillis?.() ?? 0;
      return tb - ta;
    });
    onUpdate(orders);
  });
};

export const setOrderStatus = async (orderId, status) => {
  await updateDoc(doc(db, ORDERS_COLLECTION, orderId), { status });
};

export const deleteOrder = async (orderId) => {
  await deleteDoc(doc(db, ORDERS_COLLECTION, orderId));
};

/**
 * Delete all orders (reset). Uses batched writes (max 500 per batch).
 */
export const deleteAllOrders = async () => {
  const snapshot = await getDocs(collection(db, ORDERS_COLLECTION));
  const BATCH_SIZE = 500;
  for (let i = 0; i < snapshot.docs.length; i += BATCH_SIZE) {
    const batch = writeBatch(db);
    const chunk = snapshot.docs.slice(i, i + BATCH_SIZE);
    chunk.forEach((d) => batch.delete(d.ref));
    await batch.commit();
  }
};
